import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../utils/chatIdentity';
import { PRIVACY_URL, TERMS_URL } from '../../config/loginConsent';

export default function ConsentStatusCard({ identity, onSessionExpired }) {
    const [consent, setConsent] = useState(null);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setConsent(null);
        setError('');
        const load = async () => {
            try {
                const response = await fetch(`${getApiBaseUrl()}/auth/consents`, {
                    credentials: 'include', signal: controller.signal,
                });
                const data = await response.json().catch(() => ({}));
                if (controller.signal.aborted) return;
                if (response.status === 401) onSessionExpired?.();
                if (!response.ok || typeof data.required !== 'boolean' || !data.current_version) {
                    throw new Error('Could not load your consent record. Please try again.');
                }
                setConsent(data);
            } catch (err) {
                if (!controller.signal.aborted) setError(err.message || 'Could not load your consent record.');
            }
        };
        load();
        return () => controller.abort();
    }, [identity, onSessionExpired, reload]);

    const acceptance = consent?.acceptance;
    const acceptedAt = acceptance?.created_at ? new Date(acceptance.created_at) : null;
    const dateIsValid = acceptedAt && !Number.isNaN(acceptedAt.getTime());

    return (
        <section aria-labelledby="profile-consents-heading" className="rounded-2xl border border-border-control/30 bg-surface p-5 font-brand text-fg shadow-sm">
            <h3 id="profile-consents-heading" className="text-lg font-extrabold">Consents</h3>
            <p className="mt-2 text-sm font-medium leading-relaxed text-fg-muted">
                Your <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-link underline">Terms of Use</a>,{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-link underline">Privacy Policy</a>, and account/service message acceptance are recorded separately from optional marketing preferences.
            </p>
            {!consent && !error && <p role="status" className="mt-3 text-sm text-fg-muted">Loading your consent record...</p>}
            {error && (
                <div className="mt-3">
                    <p role="alert" className="text-sm text-danger">{error}</p>
                    <button type="button" onClick={() => setReload((value) => value + 1)} className="mt-2 font-bold text-link underline focus-visible:ring-2 focus-visible:ring-focus">Try again</button>
                </div>
            )}
            {consent && (
                <div className="mt-4">
                    <p className="text-sm font-bold">{consent.required ? 'Acceptance needed at next login' : 'Current terms accepted'}</p>
                    {acceptance ? (
                        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                            <div>
                                <dt className="font-semibold text-fg-muted">Accepted on</dt>
                                <dd className="mt-1 font-medium">
                                    {dateIsValid ? <time dateTime={acceptance.created_at}>{acceptedAt.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</time> : 'Date unavailable'}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-semibold text-fg-muted">Accepted version</dt>
                                <dd className="mt-1 break-words font-medium">{acceptance.copy_version}</dd>
                            </div>
                        </dl>
                    ) : <p className="mt-2 text-sm text-fg-muted">No acceptance recorded yet.</p>}
                    {consent.required && <p className="mt-3 text-sm text-fg-muted">You will be asked to review and accept version {consent.current_version} at your next login.</p>}
                </div>
            )}
        </section>
    );
}
